import { ref, push, set } from 'firebase/database';
import { database } from '../firebase';
import { useNotifications } from '../hooks/useNotifications';

export const createNotificationDirect = async (user, notificationData) => {
    if (!user?.uid) return null;

    try {
        const notificationsRef = ref(database, `notifications/${user.uid}`);
        const newNotificationRef = push(notificationsRef);

        const notification = {
            ...notificationData,
            leida: false,
            fechaCreacion: new Date().toISOString(),
            id: newNotificationRef.key
        };

        await set(newNotificationRef, notification);
        return newNotificationRef.key;
    } catch (error) {
        console.error('Error creando notificación:', error);
        return null;
    }
};

/**
 * Servicio para generar notificaciones automáticas basadas en eventos del usuario
 */
export class NotificationService {
    constructor(user, notificationsHook = null) {
        this.user = user;
        this.notifications = notificationsHook;
    }

    async _sendNotification(data) {
        if (this.notifications?.createNotification) {
            return await this.notifications.createNotification(data);
        }
        return await createNotificationDirect(this.user, data);
    }

    /**
     * Genera notificaciones cuando se completa un tema
     */
    async notifyThemeCompleted(planName, themeName, progress) {
        if (!this.user) return;

        // Notificación de progreso
        await this._sendNotification({
            tipo: 'progreso',
            titulo: '¡Felicitaciones! 🎉',
            mensaje: `Has alcanzado el ${progress}% de progreso en tu plan "${planName}"`,
            icono: '🎯',
            datosAdicionales: { planName, progress }
        });

        // Si se completó el plan entero
        if (progress >= 100) {
            await this._sendNotification({
                tipo: 'sistema',
                titulo: '¡Plan Completado! 🎉',
                mensaje: `Has completado exitosamente el plan "${planName}". ¡Felicitaciones!`,
                icono: 'ℹ️',
                datosAdicionales: {}
            });
        }
    }

    /**
     * Genera recordatorios de estudio
     */
    async createStudyReminders(planName, pendingTopics) {
        if (!this.user || pendingTopics === 0) return;

        await this._sendNotification({
            tipo: 'estudio',
            titulo: 'Recordatorio de Estudio',
            mensaje: `Tienes ${pendingTopics} temas pendientes en tu plan "${planName}"`,
            icono: '📚',
            datosAdicionales: { planName, pendingTopics }
        });
    }

    /**
     * Notifica sobre actividad en la comunidad
     */
    async notifyCommunityActivity(message, postId = null) {
        if (!this.user) return;

        await this._sendNotification({
            tipo: 'comunidad',
            titulo: 'Actividad en Comunidad',
            mensaje,
            icono: '👥',
            datosAdicionales: { postId }
        });
    }

    /**
     * Notifica sobre exámenes próximos
     */
    async notifyExamReminder(examType, timeLeft) {
        if (!this.user) return;

        await this._sendNotification({
            tipo: 'examen',
            titulo: 'Recordatorio de Examen',
            mensaje: `Tu examen de ${examType} comienza en ${timeLeft}`,
            icono: '⏰',
            datosAdicionales: { examType, timeLeft }
        });
    }

    /**
     * Notifica sobre nuevos posts en la comunidad
     */
    async notifyNewPost(authorName, postTitle) {
        if (!this.user) return;

        const message = `${authorName} publicó: "${postTitle}"`;
        await this.notifyCommunityActivity(message);
    }

    /**
     * Notifica sobre nuevos comentarios
     */
    async notifyNewComment(authorName, postTitle) {
        if (!this.user) return;

        const message = `${authorName} comentó en: "${postTitle}"`;
        await this.notifyCommunityActivity(message);
    }

    /**
     * Notifica sobre mensajes no leídos
     */
    async notifyUnreadMessages(count) {
        if (!this.user || count === 0) return;

        const message = `Tienes ${count} mensaje${count > 1 ? 's' : ''} sin leer`;
        await this.notifyCommunityActivity(message);
    }

    /**
     * Notifica sobre logros alcanzados
     */
    async notifyAchievement(achievementName, description) {
        if (!this.user) return;

        await this._sendNotification({
            tipo: 'sistema',
            titulo: '🏆 ¡Logro Desbloqueado!',
            mensaje: `${achievementName}: ${description}`,
            icono: 'ℹ️',
            datosAdicionales: {}
        });
    }

    /**
     * Notifica sobre actualizaciones del sistema
     */
    async notifySystemUpdate(version, features) {
        if (!this.user) return;

        const message = `Nueva versión ${version} disponible con: ${features.join(', ')}`;
        await this._sendNotification({
            tipo: 'sistema',
            titulo: '🔄 Actualización Disponible',
            mensaje,
            icono: 'ℹ️',
            datosAdicionales: {}
        });
    }

    /**
     * Notifica sobre problemas de conectividad
     */
    async notifyConnectivityIssue() {
        if (!this.user) return;

        await this._sendNotification({
            tipo: 'sistema',
            titulo: '⚠️ Problema de Conectividad',
            mensaje: 'Se detectaron problemas de conexión. Algunas funciones pueden no estar disponibles.',
            icono: 'ℹ️',
            datosAdicionales: {}
        });
    }

    /**
     * Notifica sobre recuperación de conectividad
     */
    async notifyConnectivityRestored() {
        if (!this.user) return;

        await this._sendNotification({
            tipo: 'sistema',
            titulo: '✅ Conexión Restaurada',
            mensaje: 'La conexión a internet se ha restablecido. Todas las funciones están disponibles.',
            icono: 'ℹ️',
            datosAdicionales: {}
        });
    }

    /**
     * Genera recordatorios semanales de estudio
     */
    async createWeeklyStudyReminder() {
        if (!this.user) return;

        await this._sendNotification({
            tipo: 'sistema',
            titulo: '📚 Recordatorio Semanal',
            mensaje: '¡No olvides dedicar tiempo a tu estudio esta semana! La constancia es clave para el éxito.',
            icono: 'ℹ️',
            datosAdicionales: {}
        });
    }

    /**
     * Notifica sobre racha de estudio
     */
    async notifyStudyStreak(streakDays) {
        if (!this.user || streakDays < 3) return;

        const messages = {
            3: '¡3 días seguidos estudiando! Mantén el ritmo.',
            7: '¡Una semana completa de estudio! ¡Impresionante!',
            14: '¡Dos semanas de dedicación! Estás en el camino correcto.',
            30: '¡Un mes estudiando diariamente! ¡Eres un ejemplo de perseverancia!',
            50: '¡50 días consecutivos! Tu disciplina es admirable.',
            100: '¡100 días de estudio continuo! ¡Leyenda!'
        };

        const message = messages[streakDays] || `¡${streakDays} días seguidos estudiando! ¡Sigue así!`;
        await this._sendNotification({
            tipo: 'sistema',
            titulo: '🔥 Racha de Estudio',
            mensaje: message,
            icono: 'ℹ️',
            datosAdicionales: {}
        });
    }

    /**
     * Notifica sobre progreso en simulador de exámenes
     */
    async notifyExamProgress(examType, score, improvement = null) {
        if (!this.user) return;

        let message = `Completaste un examen de ${examType} con ${score}% de aciertos.`;

        if (improvement && improvement > 0) {
            message += ` ¡Mejoraste ${improvement}% respecto al anterior!`;
        } else if (improvement && improvement < 0) {
            message += ` Puedes mejorar en el próximo intento.`;
        }

        await this._sendNotification({
            tipo: 'sistema',
            titulo: '📊 Resultado de Examen',
            mensaje,
            icono: 'ℹ️',
            datosAdicionales: {}
        });
    }

    /**
     * Notifica sobre primeros pasos en la plataforma
     */
    async notifyWelcomeJourney(step) {
        if (!this.user) return;

        const welcomeMessages = {
            profile: '¡Completa tu perfil para personalizar tu experiencia!',
            firstQuiz: '¡Realiza tu primer cuestionario para comenzar tu aprendizaje!',
            firstPlan: '¡Crea tu primer plan de estudio para organizar tu progreso!',
            firstExam: '¡Intenta el simulador de exámenes para practicar!',
            community: '¡Únete a la comunidad para compartir conocimientos!'
        };

        const message = welcomeMessages[step];
        if (message) {
            await this._sendNotification({
                tipo: 'sistema',
                titulo: '🚀 Primeros Pasos',
                mensaje: message,
                icono: 'ℹ️',
                datosAdicionales: {}
            });
        }
    }
}

/**
 * Hook para usar el servicio de notificaciones
 */
export const useNotificationService = (user) => {
    const notifications = useNotifications(user);
    return new NotificationService(user, notifications);
};

/**
 * Función de utilidad para crear notificaciones desde cualquier parte de la app
 */
export const createNotification = async (user, notificationData) => {
    return await createNotificationDirect(user, notificationData);
};
